import React, { useState } from 'react';
import DashboardLayout from '../../layouts/DashboardLayout';
import TTSModulePage from './TTSModulePage';
import STTModulePage from './STTModulePage';
import MCQModulePage from './MCQModulePage';
import VMCQModulePage from './VMCQModulePage';
import FTBModulePage from './FTBModulePage';
import { AudioWaveform, MicVocal, ListChecks, Clapperboard, PenLine } from 'lucide-react';

const TABS = [
  {
    id: 'tts',
    label: 'Text to Speech',
    Icon: AudioWaveform,
    color: 'sky',
    Component: TTSModulePage,
  },
  {
    id: 'stt',
    label: 'Speech to Text',
    Icon: MicVocal,
    color: 'emerald',
    Component: STTModulePage,
  },
  {
    id: 'mcq',
    label: 'MCQ Test',
    Icon: ListChecks,
    color: 'amber',
    Component: MCQModulePage,
  },
  {
    id: 'vmcq',
    label: 'Cinematic MCQ',
    Icon: Clapperboard,
    color: 'purple',
    Component: VMCQModulePage,
  },
  {
    id: 'ftb',
    label: 'Fill the Blanks',
    Icon: PenLine,
    color: 'orange',
    Component: FTBModulePage,
  },
];

const COLOR_MAP = {
  sky:     { tab: 'bg-sky-600 text-white shadow-sky-500/30',      inactive: 'text-sky-600 dark:text-sky-400 hover:bg-sky-50 dark:hover:bg-sky-900/20',     border: 'border-sky-500',     dot: 'bg-sky-500' },
  emerald: { tab: 'bg-emerald-600 text-white shadow-emerald-500/30', inactive: 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20', border: 'border-emerald-500', dot: 'bg-emerald-500' },
  amber:   { tab: 'bg-amber-600 text-white shadow-amber-500/30',   inactive: 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20',     border: 'border-amber-500',   dot: 'bg-amber-500' },
  purple:  { tab: 'bg-purple-600 text-white shadow-purple-500/30', inactive: 'text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20',   border: 'border-purple-500',  dot: 'bg-purple-500' },
  orange:  { tab: 'bg-orange-600 text-white shadow-orange-500/30', inactive: 'text-orange-600 dark:text-orange-400 hover:bg-orange-50 dark:hover:bg-orange-900/20',   border: 'border-orange-500',  dot: 'bg-orange-500' },
};

const TestModulesPage = () => {
  const [activeTab, setActiveTab] = useState('tts');
  const activeTabDef = TABS.find(t => t.id === activeTab);
  const ActiveComponent = activeTabDef?.Component;
  const colors = COLOR_MAP[activeTabDef?.color] || COLOR_MAP.sky;

  return (
    <DashboardLayout>
      <div className="space-y-5 animate-fade-in">
        {/* Page header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Test Modules</h1>
          <p className="text-slate-500 text-sm">Manage all test modules from one place</p>
        </div>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-2 p-1 rounded-2xl bg-slate-100 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700">
          {TABS.map(tab => {
            const isActive = activeTab === tab.id;
            const tabColors = COLOR_MAP[tab.color];
            const Icon = tab.Icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 flex-1 min-w-[150px] justify-center ${
                  isActive
                    ? `${tabColors.tab} shadow-lg`
                    : `${tabColors.inactive} bg-transparent`
                }`}
              >
                <Icon className="h-4 w-4 flex-shrink-0" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Active tab indicator strip */}
        <div className={`h-0.5 rounded-full ${colors.dot} opacity-60`} />

        {/* Module content — rendered inline with embedded=true */}
        <div key={activeTab}>
          {ActiveComponent && <ActiveComponent embedded={true} />}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default TestModulesPage;
